<template>
    <CollateralMappingModal
        v-if="mappingModalOpened && !unsavedDataModalOpened"
        :additional-class-name="additionalClasses"
        :progress="loading"
        @input="save"
        @close="closingMappingModal"
    >
        <div v-if="alertConfig">
            <Alert :type="alertConfig.type">
                <FontAwesomeIcon :icon="faExclamationTriangle" />
                <div class="Alert__description">
                    {{ alertConfig.title }}
                </div>
            </Alert>
        </div>
        <CollateralMappingTable
            :collateral-entities="collateralsCopy"
            :collateral-mapping-options="collateralMappingOptionsStatus.list"
            :no-match-list="noMatchList"
            :entity-requirement-details-list="entityRequirementDetailsList"
            @show-document="showDocument"
            @update-collaterals="updateCollaterals"
            @update-no-match="updateNoMatch"
        />
    </CollateralMappingModal>
    <ConfirmationModal
        v-else-if="unsavedDataModalOpened"
        additional-class-name="CollateralMappingUnsavedModal"
        title="Want to save your changes?"
        yes="Save"
        no="Discard"
        @input="confirmUnSaveData"
    >
        Some changed values could not be validated against the field requirements. Any changes you've made will be
        discarded if you chose not to save them.
    </ConfirmationModal>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { cloneDeep, isEqual } from 'lodash';

    import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import { Alert } from '@evidentid/dashboard-commons/components/Alert';
    import { AlertConfig } from '@evidentid/dashboard-commons/components/Alert/types';
    import { EntityRequirementDetails, TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';
    import { CollateralEntity } from '@evidentid/tprm-portal-lib/models/entity-details/Collateral/CollateralEntity.model';
    import { CollateralMappingOptions } from '@evidentid/tprm-portal-lib/models/entity-details/Collateral/CollateralMappingOptions.model';
    import { CollateralMappingType } from '@evidentid/tprm-portal-lib/models/entity-details/Collateral/CollateralMappingType.model';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import ConfirmationModal from '@/components/confirmation-modal/ConfirmationModal.vue';
    import { CategorizedEnumLabels } from '@/modules/dashboard/models/CategorizedEnumLabels.model';
    import { EnumCategories } from '@/modules/dashboard/models/EnumCategories.model';
    import { useDecisioningCriteriaStore } from '@/modules/decisioning-criteria/store';
    import CollateralMappingModal
        from '@/modules/entity-details/components/CollateralMappingModal/CollateralMappingModal.vue';
    import CollateralMappingTable
        from '@/modules/entity-details/components/CollateralMappingTable/CollateralMappingTable.vue';
    import { useEntityDetailsStore } from '@/modules/entity-details/store';
    import {
        isCollateralMappingHasNoMatch,
    } from '@/modules/entity-details/utils/isCollateralMappingHasNoMatch/isCollateralMappingHasNoMatch';
    import { getEnumDefaultDisplayValue } from '@/utils/getEnumDefaultDisplayValue';
    import { useDashboardStore } from '@/modules/dashboard/vuex/dashboard';

    const componentName = 'CollateralMappingProcedure';
    // PRODUCT-26110 QA fixes4: declares every event this procedure component `$emit`s --
    // without a declared `emits`, Vue 3 warns "Component emitted event 'abort'/'finish'
    // but it is neither declared in the emits option nor as an 'onAbort'/'onFinish' prop"
    // every time it fires.
    export default defineComponent({
        name: componentName,
        components: {
            Alert,
            CollateralMappingModal,
            CollateralMappingTable,
            ConfirmationModal,
            FontAwesomeIcon,
        },
        // PRODUCT-26110 QA fixes4: `uuid` is unused; declared purely so Vue 3 doesn't warn
        // "Extraneous non-props attributes (uuid)" (see UploadEntityDocumentProcedure.vue).
        props: {
            uuid: {
                type: String,
                default: '',
            },
            collaterals: {
                type: Array as PropType<CollateralEntity[]>,
                required: true,
                default: (): CollateralEntity[] => [],
            },
            entityContactEmail: {
                type: String as PropType<string>,
                default: '',
            },
            entityId: {
                type: String as PropType<string>,
                default: '',
            },
        },
        emits: [ 'abort', 'finish' ],
        data() {
            return {
                faExclamationTriangle,
                showingDocument: false,
                mappingModalOpened: true,
                unsavedDataModalOpened: false,
                // keeps records of no matched collateral entities id that already existed in db
                initialNoMatchList: [] as string[],
                // initially the same as initialNoMatchList, but also keep tracks on current session noMatch selections
                // the reason to have two lists is to not lose the initial state so that we know when to we actually
                // have no match selection made from current modal session rather than previous saved state.
                noMatchList: [] as string[],
                collateralsCopy: [] as CollateralEntity[],
                alertConfig: null as AlertConfig | null,
            };
        },
        computed: {
            loadingEntityRiskProfiles(): boolean {
                return useDecisioningCriteriaStore()
                    .entityRiskProfiles[this.rpName][this.entityId].status === OperationStatus.loading;
            },
            loadingCollateralEntities(): boolean {
                return useEntityDetailsStore()
                    .collateralEntitiesStatus[this.rpName].status === OperationStatus.loading;
            },
            loadingEntityDetails(): boolean {
                return useEntityDetailsStore()
                    .requirementDetailsListStatus[this.rpName].status === OperationStatus.loading;
            },
            loadingCollateralMappingOptions(): boolean {
                return this.collateralMappingOptionsStatus.status === OperationStatus.loading;
            },
            patchingCollateralEntity(): boolean {
                return this.patchCollateralsStatus === OperationStatus.loading;
            },
            loading(): boolean {
                return this.loadingCollateralMappingOptions ||
                    this.loadingEntityRiskProfiles ||
                    this.loadingCollateralEntities ||
                    this.loadingEntityDetails ||
                    this.patchingCollateralEntity;
            },
            rpName(): string {
                // TODO(PRODUCT-18675): remove any after the migration to the newer version. apply to whole file
                return (this as any).$rp.current!;
            },
            categorizedEnumLabels(): CategorizedEnumLabels {
                return useDashboardStore().categorizedEnumLabels;
            },
            collateralMappingOptionsStatus(): { status: OperationStatus, list: CollateralMappingOptions[] } {
                return useEntityDetailsStore().collateralMappingOptionsStatus[this.rpName] || {
                    status: OperationStatus.uninitialized,
                    list: [],
                };
            },
            patchCollateralsStatus(): OperationStatus {
                return useEntityDetailsStore().patchCollateralStatus[this.rpName] ||
                    OperationStatus.uninitialized;
            },
            additionalClasses(): string {
                const hide = this.showingDocument ? ' invisible' : '';
                return `${componentName}${hide}`;
            },
            entityRequirementDetailsList(): EntityRequirementDetails[] {
                return useEntityDetailsStore()
                    .requirementDetailsListStatus[this.rpName]?.list as EntityRequirementDetails[] || [];
            },
        },
        watch: {
            collaterals: {
                immediate: true,
                handler() {
                    this.collateralsCopy = cloneDeep(this.collaterals);
                    this.initialNoMatchList = this.collateralsCopy
                        .filter(isCollateralMappingHasNoMatch)
                        .map((collateral) => collateral.evaluatedEntityId);
                    this.noMatchList = [ ...this.initialNoMatchList ];
                },
            },
        },
        mounted() {
            useEntityDetailsStore().loadCollateralMappingOptions({
                rpName: this.rpName,
                entityId: this.entityId || '',
            });
        },
        unmounted(): void {
            useEntityDetailsStore().clearCollateralMappingOptions({ rpName: this.rpName });
        },
        methods: {
            async save(): Promise<void> {
                this.alertConfig = null;
                const collateralsToPatch = this.collateralsCopy.map(this.getCollateralsNeedPatch).filter(Boolean);
                if (collateralsToPatch.length === 0) {
                    this.close();
                } else {
                    await this.patchCollaterals(collateralsToPatch as CollateralEntity[]);
                    if (this.patchCollateralsStatus === OperationStatus.error) {
                        this.alertConfig = {
                            title: 'Something went wrong. Please try again.',
                            type: 'danger',
                        };
                        return;
                    }
                    await this.reloadEntityDetailsData();
                    this.$emit('finish');
                }
            },
            close(): void {
                this.$emit('abort');
                this.mappingModalOpened = false;
            },
            showDocument(requirementDetails: EntityRequirementDetails): void {
                if (!requirementDetails.coverage) {
                    console.error('No coverage details found for collateral', requirementDetails);
                    return;
                }
                this.showingDocument = true;
                // TODO(PRODUCT-18675): remove any after the migration to the newer version. apply to whole file
                (this as any).$procedures.execute('showEntityRequirementRprDocuments', {
                    rpName: this.rpName,
                    entityId: this.entityId,
                    email: this.entityContactEmail || '',
                    requirementType: requirementDetails.coverageType,
                    verificationId: requirementDetails.coverage.requestId,
                }, this.onDocumentClosed);
            },
            onDocumentClosed(): void {
                this.showingDocument = false;
            },
            updateCollaterals(collaterals: CollateralEntity[]): void {
                this.collateralsCopy = [ ...collaterals ];
            },
            updateNoMatch(evaluatedEntityId: string): void {
                this.noMatchList = this.noMatchList.includes(evaluatedEntityId)
                    ? this.noMatchList.filter((x) => x !== evaluatedEntityId)
                    : [ ...this.noMatchList, evaluatedEntityId ];
            },
            getCollateralsNeedPatch(
                collateralEntity: CollateralEntity,
                originalCollateralIndex: number,
            ): CollateralEntity | null {
                const currentlyNoMatch = this.noMatchList.includes(collateralEntity.evaluatedEntityId);
                const mappingDirtied =
                    !isEqual(collateralEntity.mapping, this.collaterals[originalCollateralIndex].mapping);
                const noMatchOnLoad = this.initialNoMatchList.includes(collateralEntity.evaluatedEntityId);
                const noMatchSetDuringModalSession = !noMatchOnLoad && currentlyNoMatch;
                const needsPatch = mappingDirtied || noMatchSetDuringModalSession;
                const newCollateralMappingOption = noMatchSetDuringModalSession
                    ? null
                    : collateralEntity.mapping?.collateral || null;
                const mapping = !currentlyNoMatch && collateralEntity.mapping === null
                    ? null
                    : {
                        mappingType: CollateralMappingType.manual,
                        collateral: newCollateralMappingOption,
                    };
                return needsPatch ? { ...collateralEntity, mapping } : null;
            },
            async patchCollaterals(collateralsToPatch: CollateralEntity[]): Promise<void> {
                const collateralPatchObjects = collateralsToPatch.map(
                    (collateral) => ({
                        evaluatedEntityId: collateral.evaluatedEntityId,
                        mapping: collateral.mapping,
                    }),
                );
                await useEntityDetailsStore().patchCollaterals(
                    { rpName: this.rpName, entityId: this.entityId, collateralPatchObjects },
                );
            },
            async reloadEntityDetailsData(): Promise<void> {
                await Promise.all([
                    useDashboardStore().loadEntity({ rpName: this.rpName, id: this.entityId }),
                    useEntityDetailsStore().loadRequirementDetailsList(
                        { rpName: this.rpName, entityId: this.entityId },
                    ),
                    useEntityDetailsStore().loadCollateralEntities(
                        { rpName: this.rpName, entityId: this.entityId },
                    ),
                    useDecisioningCriteriaStore().loadEntityRiskProfiles({
                        rpName: this.rpName,
                        entityId: this.entityId,
                    }),
                ]);
            },
            closingMappingModal(): void {
                const collateralsToPatch = this.collateralsCopy.map(this.getCollateralsNeedPatch).filter(Boolean);
                if (collateralsToPatch.length > 0) {
                    this.unsavedDataModalOpened = true;
                    this.mappingModalOpened = false;
                } else {
                    this.close();
                }
            },
            confirmUnSaveData(confirmed: boolean): void {
                if (!confirmed) {
                    this.close();
                }
                this.unsavedDataModalOpened = false;
                this.mappingModalOpened = true;
            },
        },
    });
</script>
